import { OrgData, ProfileAssessment } from '@/models/Orgs'
import {
  Alert,
  Typography,
  TextField,
  Box,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material'
import {
  AutoAwesome,
  AutoAwesome as AutoAwesomeIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  generateProfile,
  triggerProfileRegen,
  updateProfileText,
  appendDescriptionAndGenerate,
  listProfileRevisions,
  getProfileRevision,
} from '@/api/orgsAPI'
import ProfileRevisionHistoryDialog from './ProfileRevisionHistoryDialog'
import { useProfileRegenGuard } from './ProfileRegenBlockProvider'
import { useQueryClient } from '@tanstack/react-query'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import Loading from './Loading'
import SpeechToText from './SpeechToText'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import { useSSEProgress } from '@/pages/onboarding/hooks/useSSEProgress'
import { useProfileRegenPoll } from '@/utils/hooks/orgs'

interface GenerateProfileProps {
  organisation: OrgData
  token: string
  onProfileSaved?: () => void
  disabled?: boolean
  onUnsavedChange?: (hasUnsaved: boolean) => void
}

export default function GenerateEditProfile({
  organisation,
  token,
  onProfileSaved,
  disabled,
  onUnsavedChange,
}: GenerateProfileProps) {
  const { user, isLoading } = getCurrentUser()
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [success, setSuccess] = useState(false)
  const [saving, setSaving] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [showProfileWarning, setShowProfileWarning] = useState(false)
  const [profileDataAssessment, setProfileDataAssessment] =
    useState<null | ProfileAssessment>(null)
  const [profileTextAssessment, setProfileTextAssessment] =
    useState<null | ProfileAssessment>(null)
  const [currentDataSuggestionIndex, setCurrentDataSuggestionIndex] =
    useState(0)
  const [currentTextSuggestionIndex, setCurrentTextSuggestionIndex] =
    useState(0)
  const [wizardAnswers, setWizardAnswers] = useState<Record<number, string>>({})
  const [regenerating, setRegenerating] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  // What the latest completed auto-regen changed (shown once after it finishes).
  const [lastChange, setLastChange] = useState<{
    changes: string[]
    warnings: string[]
  } | null>(null)
  // Why the latest auto-regen failed (insufficient_data vs a transient error) and,
  // for insufficient data, the suggestion questions it computed — so the recovery
  // wizard can open instantly without regenerating.
  const [failInfo, setFailInfo] = useState<{
    reason: string | null
    message: string | null
    assessment: ProfileAssessment | null
  } | null>(null)
  const [profileJobId, setProfileJobId] = useState<string | null>(null)
  const [skipNextProfileAssessment, setSkipNextProfileAssessment] =
    useState(false)
  const hasProfile = Boolean(organisation.profile_text)
  // A background auto-regeneration is running / last one failed. Drives the
  // "updating…" + retry UI and disables actions that would race the regen.
  const isRegenerating = organisation.profile_regen_status === 'in_progress'
  const regenFailed = organisation.profile_regen_status === 'failed'
  // "pending" is set at upload accept, before ingestion + the regen — the profile
  // is about to change, so it reads as "updating" everywhere the regen does.
  const isPending = organisation.profile_regen_status === 'pending'

  useProfileRegenPoll(user, organisation.uuid, isPending || isRegenerating)

  const { canWrite } = usePausedOrg()
  const guardRegen = useProfileRegenGuard()
  const location = useLocation()
  const navigate = useNavigate()
  const insufficientData = failInfo?.reason === 'insufficient_data'

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [profileText, setProfileText] = useState(
    organisation.profile_text || '',
  )
  // Empty box → show "Regenerate from scratch" instead of Save, and block empty saves.
  const profileIsEmpty = profileText.trim() === ''
  const queryClient = useQueryClient()
  const profileProgress = useSSEProgress({
    type: 'profile',
    jobId: profileJobId,
    token,
    onError: () => {
      setGenerating(false)
      setSaving(false)
      setError(
        new Error(
          "We couldn't finish updating your profile — please try again in a moment.",
        ),
      )
      setProfileJobId(null)
    },
  })

  function applyProfileGenerationResult(response: any) {
    if (response.assessment && !skipNextProfileAssessment) {
      setSaving(false)
      setProfileDataAssessment(response.assessment)
      setCurrentDataSuggestionIndex(0)
      setWizardAnswers({})
    }

    if (response.profile) {
      queryClient.invalidateQueries({ queryKey: ['currentOrgs', user?.uuid] })
      queryClient.invalidateQueries({
        queryKey: ['onboardingData', user?.uuid, false, organisation.uuid],
      })
      setGenerating(false)
      setSuccess(true)
      setSaving(false)
      if (onProfileSaved) {
        onProfileSaved()
      }
    } else {
      setGenerating(false)
    }
    setSkipNextProfileAssessment(false)
    setProfileJobId(null)
  }

  useEffect(() => {
    if (profileProgress.status !== 'completed') return
    applyProfileGenerationResult(profileProgress.data)
  }, [profileProgress.status, profileProgress.data])

  // Sync the box to the org's profile only when the profile TEXT changes — a regen
  // landing (its new profile wins; the field is locked while it runs, so it's not
  // silent) — not on every org-object churn (status flips, focus/mount + poll
  // refetches), which would otherwise reset the box mid-edit with no regen at all.
  useEffect(() => {
    setProfileText(organisation.profile_text || '')
  }, [organisation.profile_text])

  // A regen can be kicked off elsewhere — a doc upload on another tab, or by
  // another user on the same org — while this tab sits on cached idle. Refetch the
  // org on mount and whenever the tab regains focus so an in_progress regen is
  // picked up here (the status poll then keeps it live until it resolves).
  useEffect(() => {
    const refetch = () =>
      queryClient.invalidateQueries({ queryKey: ['currentOrgs', user?.uuid] })
    refetch()
    window.addEventListener('focus', refetch)
    return () => window.removeEventListener('focus', refetch)
  }, [queryClient, user?.uuid, organisation.uuid])

  useEffect(() => {
    if (profileText !== organisation.profile_text) {
      if (profileText.trim() === '' && organisation.profile_text === null) {
        setHasUnsavedChanges(false)
      } else {
        setHasUnsavedChanges(true)
      }
      setSuccess(false)
      setError(null)
    } else {
      setHasUnsavedChanges(false)
    }
  }, [profileText, organisation.profile_text])

  // Report unsaved profile-text edits up to ProfilePage's nav blocker. onUnsavedChange
  // is a fresh inline callback each render, so keep it in a ref and depend on the value.
  const onUnsavedChangeRef = useRef(onUnsavedChange)
  onUnsavedChangeRef.current = onUnsavedChange
  useEffect(() => {
    onUnsavedChangeRef.current?.(hasUnsavedChanges)
    return () => onUnsavedChangeRef.current?.(false)
  }, [hasUnsavedChanges])

  // Surface a just-completed background regen's result once — even if the user
  // wasn't on this tab while it ran (fast/no-op regens finish before they look).
  // When idle, check the latest revision: if it's a recent, unseen system regen,
  // show its "what changed" (or "no changes"). Keyed by version in localStorage so
  // it shows once; recency-gated so an old revision isn't announced on first load.
  useEffect(() => {
    if (isRegenerating || isPending || generating || !hasProfile) return
    let cancelled = false
    listProfileRevisions(token, organisation.uuid)
      .then((revs) => {
        const latest = revs[0]
        if (
          cancelled ||
          !latest ||
          latest.status !== 'saved' ||
          (latest.data_entry_by !== 'auto' &&
            latest.data_entry_by !== 'regenerate')
        ) {
          return
        }
        // Timestamps are naive-UTC — parse as UTC so the age is accurate.
        const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(latest.date)
          ? latest.date
          : `${latest.date}Z`
        if (Date.now() - new Date(iso).getTime() > 15 * 60 * 1000) return
        const seenKey = `profileRegenSeen:${organisation.uuid}`
        if (latest.version <= Number(localStorage.getItem(seenKey) || 0)) return
        getProfileRevision(token, organisation.uuid, latest.version)
          .then((detail) => {
            if (cancelled) return
            setLastChange({
              changes: detail.changes_summary ?? [],
              warnings: detail.preservation_warnings ?? [],
            })
            localStorage.setItem(seenKey, String(latest.version))
          })
          .catch(() => {
            /* non-fatal — the profile still updated */
          })
      })
      .catch(() => {
        /* non-fatal */
      })
    return () => {
      cancelled = true
    }
  }, [
    isRegenerating,
    isPending,
    generating,
    hasProfile,
    token,
    organisation.uuid,
  ])

  // Whenever the org is in a failed-regen state, fetch the reason from the latest
  // revision so the banner can offer the right recovery (also covers landing here
  // already-failed, e.g. via a consumer's "Go to profile"). Cleared on recovery.
  useEffect(() => {
    if (!regenFailed) {
      setFailInfo(null)
      return
    }
    if (failInfo) return
    listProfileRevisions(token, organisation.uuid)
      .then((revs) => {
        const latest = revs[0]
        if (latest && latest.status === 'failed') {
          return getProfileRevision(token, organisation.uuid, latest.version)
        }
        return null
      })
      .then((detail) => {
        setFailInfo(
          detail
            ? {
                reason: detail.failure_reason,
                message: detail.error_message,
                assessment: detail.assessment,
              }
            : // No stored detail — assume a transient error so the auto-retry can
              // still fire rather than leaving the field stuck "updating".
              { reason: 'error', message: null, assessment: null },
        )
      })
      .catch(() => {
        // Couldn't load the reason — same fallback, so a fetch blip doesn't strand
        // the field "updating".
        setFailInfo({ reason: 'error', message: null, assessment: null })
      })
  }, [regenFailed, failInfo, token, organisation.uuid])

  // Recovery for an insufficient-data failure: show the data-suggestion wizard
  // using the questions the failed job already computed — no waiting on another
  // regeneration. Falls back to a fresh generate if we somehow have no stored
  // questions (older failures, or the assessment couldn't be saved).
  function openSuggestionsWizard() {
    if (failInfo?.assessment && failInfo.assessment.Suggestions.length > 0) {
      setError(null)
      setProfileDataAssessment(failInfo.assessment)
      setCurrentDataSuggestionIndex(0)
      setWizardAnswers({})
      return
    }
    handleGenerateProfile()
  }

  // Auto-open the stored recovery questions after an insufficient-data failure so
  // the user needn't click. Only opens when questions are stored — never auto-runs
  // a fresh generate.
  useEffect(() => {
    if (
      !insufficientData ||
      profileDataAssessment ||
      generating ||
      !failInfo?.assessment?.Suggestions?.length
    ) {
      return
    }
    // Clear the recover flag if we arrived from a consumer's block dialog.
    if ((location.state as { recoverProfile?: boolean })?.recoverProfile) {
      navigate(location.pathname, { replace: true, state: {} })
    }
    openSuggestionsWizard()
  }, [
    insufficientData,
    profileDataAssessment,
    generating,
    failInfo,
    location.state,
    navigate,
  ])

  // Auto-fire the retry once on a transient (error) failure so the user doesn't
  // have to click. Insufficient-data failures open the wizard instead (above); we
  // retry only once, so if it fails again the failed-state retry button stays for a
  // manual attempt rather than looping.
  const autoRetriedRef = useRef(false)
  // The auto-retry has been kicked off and its status refetched — so a failed retry
  // is recognised even when it failed too fast for the poll to observe in_progress.
  const [retryCallDone, setRetryCallDone] = useState(false)
  // The auto-retry ran and the profile ended up failed again → surface the manual
  // "Update profile" fallback. Until then we show "updating", not "press Update".
  const [autoRetryFailed, setAutoRetryFailed] = useState(false)
  useEffect(() => {
    if (!regenFailed) {
      // Reset only on a clean idle result, never during in_progress — otherwise a
      // failed retry (failed → in_progress → failed) would re-fire and loop.
      if (organisation.profile_regen_status === 'idle') {
        autoRetriedRef.current = false
        setRetryCallDone(false)
        setAutoRetryFailed(false)
      }
      return
    }
    if (
      insufficientData ||
      !hasProfile ||
      generating ||
      regenerating ||
      autoRetriedRef.current ||
      !failInfo // wait until the failure reason is known
    ) {
      return
    }
    // Clear the recover flag if we arrived from a consumer's block dialog.
    if ((location.state as { recoverProfile?: boolean })?.recoverProfile) {
      navigate(location.pathname, { replace: true, state: {} })
    }
    autoRetriedRef.current = true
    handleTriggerProfileUpdate()
  }, [
    regenFailed,
    insufficientData,
    hasProfile,
    generating,
    regenerating,
    failInfo,
    organisation.profile_regen_status,
    location.state,
    navigate,
  ])

  // Once the auto-retry has been kicked off + refetched and the profile is still
  // failed, surface the manual fallback — not gated on observing in_progress, which
  // a retry failing within one poll window would skip (leaving the field stuck).
  useEffect(() => {
    if (regenFailed && autoRetriedRef.current && retryCallDone) {
      setAutoRetryFailed(true)
    }
  }, [regenFailed, retryCallDone])

  // "Updating" covers a running regen and the brief auto-retry window after a
  // transient failure. The lock/blur and the banner share this flag so the field
  // stays blurred across the failure→retry gap instead of flickering.
  const isUpdating =
    isPending ||
    isRegenerating ||
    (regenFailed && !insufficientData && !autoRetryFailed)

  async function handleGenerateProfile() {
    setGenerating(true)
    setSaving(true)
    setError(null)
    setSuccess(false)
    setProfileDataAssessment(null)
    setProfileTextAssessment(null)

    try {
      const response = await generateProfile(token, organisation.uuid)
      setSkipNextProfileAssessment(false)
      setProfileJobId(response.job_id)
    } catch (err) {
      setGenerating(false)
      setSaving(false)
      if (guardRegen(err)) return
      setError(
        new Error(
          "We couldn't finish updating your profile — please try again in a moment.",
        ),
      )
      return
    }
  }

  async function handleSaveProfile(force?: boolean) {
    // Never save an empty profile (backstops the empty-box → Regenerate UI swap).
    if (!profileText.trim()) {
      setError(
        new Error(
          'Add some profile text before saving, or use "Regenerate from scratch".',
        ),
      )
      return
    }
    setSaving(true)
    setSuccess(false)
    setError(null)

    setProfileDataAssessment(null)
    setProfileTextAssessment(null)

    if (force) {
      setShowProfileWarning(false)

      let response
      try {
        response = await updateProfileText(
          token,
          organisation.uuid,
          profileText,
          true, // force submit
        )
      } catch (err) {
        setSaving(false)
        if (guardRegen(err)) return
        setError(new Error('Error saving profile'))
        return
      }
      if (response && !response.error) {
        queryClient.invalidateQueries({ queryKey: ['currentOrgs', user?.uuid] })
        // Invalidate onboarding data to update completion status
        queryClient.invalidateQueries({
          queryKey: ['onboardingData', user?.uuid, false, organisation.uuid],
        })
        setSaving(false)
        setGenerating(false)
        setSuccess(true)

        // Call the onProfileSaved callback if provided
        if (onProfileSaved) {
          onProfileSaved()
        }
      } else {
        setSaving(false)
        setError(new Error(response?.error || 'Error saving profile'))
      }

      return
    }
    // char count without spaces
    const textLength = profileText.replace(/\s+/g, '').length

    if (textLength === 0) {
      setShowProfileWarning(true)
      setSaving(false)
      return
    }

    if (textLength < 2000) {
      setShowProfileWarning(true)
    }

    let response
    try {
      response = await updateProfileText(token, organisation.uuid, profileText)
    } catch (err) {
      setSaving(false)
      if (guardRegen(err)) return
      setError(new Error('Error saving profile'))
      return
    }

    // if the response is an assessment
    if (response && response.assessment) {
      setProfileTextAssessment(response.assessment)
      setCurrentTextSuggestionIndex(0)
      setSaving(false)
      setTimeout(() => {
        const el = document.getElementById(
          'profile-text-suggestion-wizard-title',
        )
        if (el) el.focus()
      }, 0)
      return
    }

    if (response && response.success) {
      queryClient.invalidateQueries({ queryKey: ['currentOrgs', user?.uuid] })
      // Invalidate onboarding data to update completion status
      queryClient.invalidateQueries({
        queryKey: ['onboardingData', user?.uuid, false, organisation.uuid],
      })
      setSaving(false)
      setGenerating(false)
      setSuccess(true)

      // Call the onProfileSaved callback if provided
      if (onProfileSaved) {
        onProfileSaved()
      }
    } else {
      setSaving(false)
      setError(new Error(response?.error || 'Error saving profile'))
    }
  }

  // Manual "Update profile" (used for retry after a failed auto-regen): kicks off
  // a background regeneration that auto-saves the result. We then poll the org
  // (profile_regen_status) to reflect progress and pick up the new profile.
  async function handleTriggerProfileUpdate() {
    setRegenerating(true)
    setRetryCallDone(false)
    setError(null)
    setSuccess(false)
    try {
      await triggerProfileRegen(token, organisation.uuid)
      // Await the refetch so profile_regen_status has flipped to in_progress
      // before we drop the "Starting…" state — otherwise the button snaps back to
      // "Update profile" between the POST and the refetch and looks unresponsive.
      await queryClient.invalidateQueries({
        queryKey: ['currentOrgs', user?.uuid],
      })
      // Retry kicked off + status refetched: a 'failed' status from here on is a
      // failed retry (even if it failed too fast for the poll to see in_progress).
      setRetryCallDone(true)
    } catch (err) {
      if (guardRegen(err)) return
      // The retry couldn't even start. Mark it terminal so the field un-locks and
      // the manual "Update profile" fallback shows — otherwise it stays "updating"
      // forever waiting for a regen that never ran.
      setAutoRetryFailed(true)
      setError(
        new Error('Could not start the profile update. Please try again.'),
      )
    } finally {
      setRegenerating(false)
    }
  }

  async function handleAppendAndRegenerate(
    dataActiveSuggestions: { text: string }[],
    skipNewAssessment: boolean,
  ) {
    const answersText = dataActiveSuggestions
      .map((_, i) => wizardAnswers[i]?.trim() || null)
      .filter(Boolean)
      .join('\n\n')

    if (!answersText) {
      setProfileDataAssessment(null)
      return
    }

    setGenerating(true)
    setSaving(true)
    setError(null)

    try {
      const response = await appendDescriptionAndGenerate(
        token,
        organisation.uuid,
        answersText,
      )

      setProfileDataAssessment(null)
      setSkipNextProfileAssessment(skipNewAssessment)
      setProfileJobId(response.job_id)
    } catch (err) {
      setGenerating(false)
      setSaving(false)
      if (guardRegen(err)) return
      setError(new Error('Error regenerating profile'))
    }
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <Box sx={{ mb: 3, pl: 2 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: '400',
            color: '#333',
            fontSize: isMobile ? '14px' : '16px',
          }}
        >
          <strong>Your profile is key to successful funding matches.</strong>{' '}
          <br />
          Creating a detailed profile dramatically improves the quality and
          relevance of your funding opportunities.
        </Typography>
        {/* <Typography variant="caption" mt={2}>
          Use this space to provide wide-ranging information about your
          organisation, your work, and the causes you serve. The information you
          provide here will be used to match you with funding opportunities.
        </Typography> */}

        {showProfileWarning && (
          <Dialog
            open={showProfileWarning}
            onClose={() => setShowProfileWarning(false)}
            aria-labelledby="warning-dialog-title"
            aria-describedby="warning-dialog-description"
          >
            <DialogTitle id="warning-dialog-title">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src={`/Thumbs-down400px.png`}
                  alt="Thumbs down"
                  style={{
                    width: '40px',
                    height: '40px',
                    marginRight: '20px',
                    marginLeft: '10px',
                    marginTop: '10px',
                  }}
                />
                <Typography variant="h6" sx={{ mt: 1 }}>
                  Your profile is looking a little lean!
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText
                id="warning-dialog-description"
                sx={{ color: '#1C3045' }}
              >
                Add more information to get better funding matches.
                <br />
                Alternatively, you can generate one as a starting point and
                adjust it if you need to.
                <br />
                <br />
                Some ideas for what to include:
                <ul>
                  <li>Mission and Vision</li>
                  <li>Community Engagement and Impact</li>
                  <li>Objectives</li>
                  <li>Programs and Services</li>
                  <li>Notable Achievements</li>
                </ul>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setShowProfileWarning(false)}
                variant="contained"
                disableRipple
                sx={{
                  color: 'white',
                  bgcolor: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  width: '100px',
                }}
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {profileDataAssessment &&
          (() => {
            const dataActiveSuggestions = profileDataAssessment.Suggestions.map(
              (s) => ({ text: s }),
            )
            if (dataActiveSuggestions.length === 0) return null
            const idx = Math.min(
              currentDataSuggestionIndex,
              dataActiveSuggestions.length - 1,
            )
            const current = dataActiveSuggestions[idx]
            const isLast = idx === dataActiveSuggestions.length - 1
            const isBadDataQuality = profileDataAssessment.Quality === 'Bad'
            const hasAnyAnswer = Object.values(wizardAnswers).some((v) =>
              v.trim(),
            )

            return (
              <Box
                id="profile-data-suggestion-wizard-title"
                role="region"
                aria-labelledby="profile-data-suggestion-wizard-title"
                tabIndex={-1}
                sx={{
                  mt: 3,
                  mb: 2,
                  border: '1px solid #c2d9f0',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: '#1C3045', mb: 0.5 }}
                >
                  {profileDataAssessment.Quality === 'Bad'
                    ? 'We need some more information from you to generate a profile'
                    : 'Tell us more to generate a better profile'}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Read through the suggestions below and add more detail to
                  improve your chances with funders.
                </Typography>

                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: '#1976d2' }}
                  >
                    Suggestions ({idx + 1} of {dataActiveSuggestions.length})
                  </Typography>
                </Box>
                <Box
                  sx={{
                    borderLeft: '3px solid #1976d2',
                    pl: 2,
                    py: 1,
                    backgroundColor: '#f5f9ff',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Try to answer this question:{' '}
                    <Box component="span" sx={{ fontWeight: 400 }}>
                      {current.text}
                    </Box>
                  </Typography>
                </Box>

                <SpeechToText
                  token={token}
                  disabled={generating}
                  onTranscription={(text) =>
                    setWizardAnswers((prev) => ({
                      ...prev,
                      [idx]: prev[idx] ? `${prev[idx]}\n\n${text}` : text,
                    }))
                  }
                />
                <TextField
                  multiline
                  minRows={4}
                  maxRows={8}
                  fullWidth
                  placeholder="Add more detail here..."
                  value={wizardAnswers[idx] ?? ''}
                  onChange={(e) =>
                    setWizardAnswers((prev) => ({
                      ...prev,
                      [idx]: e.target.value,
                    }))
                  }
                  sx={{ mt: 1, mb: 2 }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {idx > 0 ? (
                    <Button
                      size="small"
                      variant="text"
                      disableRipple
                      onClick={() => setCurrentDataSuggestionIndex(idx - 1)}
                      sx={{ textTransform: 'none', fontSize: '13px' }}
                    >
                      <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} /> Previous
                    </Button>
                  ) : (
                    <Box />
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {!isLast && !isBadDataQuality && (
                      <Button
                        size="small"
                        variant="text"
                        disableRipple
                        onClick={() => setCurrentDataSuggestionIndex(idx + 1)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '13px',
                          color: 'text.secondary',
                        }}
                      >
                        Skip
                      </Button>
                    )}
                    {isLast ? (
                      <Tooltip
                        title={
                          isBadDataQuality && !hasAnyAnswer
                            ? 'Please answer the questions above to continue'
                            : ''
                        }
                      >
                        <span>
                          <Button
                            variant="contained"
                            disableRipple
                            onClick={() =>
                              handleAppendAndRegenerate(
                                dataActiveSuggestions,
                                !isBadDataQuality,
                              )
                            }
                            disabled={
                              generating || (isBadDataQuality && !hasAnyAnswer)
                            }
                            sx={{
                              bgcolor: '#1C3045',
                              color: 'white',
                              textTransform: 'none',
                              '&:hover': { bgcolor: '#152433' },
                            }}
                            startIcon={
                              generating && (
                                <CircularProgress size={14} color="inherit" />
                              )
                            }
                          >
                            {generating
                              ? 'Regenerating...'
                              : 'Add Answers and Regenerate'}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="contained"
                        disableRipple
                        onClick={() => setCurrentDataSuggestionIndex(idx + 1)}
                        sx={{
                          bgcolor: '#1976d2',
                          color: 'white',
                          textTransform: 'none',
                          '&:hover': { bgcolor: '#1565c0' },
                        }}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            )
          })()}
        {profileTextAssessment &&
          (() => {
            const textActiveSuggestions = [
              ...profileTextAssessment.Suggestions.map((s) => ({
                text: s,
                type: 'suggestion' as const,
              })),
            ]
            if (textActiveSuggestions.length === 0) return null
            const idx = Math.min(
              currentTextSuggestionIndex,
              textActiveSuggestions.length - 1,
            )
            const current = textActiveSuggestions[idx]
            const isLast = idx === textActiveSuggestions.length - 1
            const isBadTextQuality = profileTextAssessment.Quality === 'Bad'

            return (
              <Box
                id="profile-text-suggestion-wizard-title"
                role="region"
                aria-labelledby="profile-text-suggestion-wizard-title"
                tabIndex={-1}
                sx={{
                  mt: 3,
                  mb: 2,
                  border: '1px solid #c2d9f0',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: '#1C3045', mb: 0.5 }}
                >
                  Here are some suggestions for a better profile
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Read through the suggestions below and add more detail to
                  improve your chances with funders.
                </Typography>

                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: '#1976d2' }} />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: '#1976d2' }}
                  >
                    Suggestions ({idx + 1} of {textActiveSuggestions.length})
                  </Typography>
                </Box>
                <Box
                  sx={{
                    borderLeft: '3px solid #1976d2',
                    pl: 2,
                    py: 1,
                    backgroundColor: '#f5f9ff',
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: '#1C3045' }}
                  >
                    Try answering:{' '}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {current.text}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2,
                  }}
                >
                  {idx > 0 ? (
                    <Button
                      size="small"
                      variant="text"
                      disableRipple
                      onClick={() => setCurrentTextSuggestionIndex(idx - 1)}
                      sx={{ textTransform: 'none', fontSize: '13px' }}
                    >
                      <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} /> Previous
                    </Button>
                  ) : (
                    <Box />
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {!isLast && !isBadTextQuality && (
                      <Button
                        size="small"
                        variant="text"
                        disableRipple
                        onClick={() => handleSaveProfile(true)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '13px',
                          color: 'text.secondary',
                        }}
                      >
                        Skip all
                      </Button>
                    )}
                    {isLast ? (
                      <Button
                        variant="contained"
                        disableRipple
                        onClick={() =>
                          handleSaveProfile(isBadTextQuality ? undefined : true)
                        }
                        sx={{
                          bgcolor: '#1C3045',
                          color: 'white',
                          textTransform: 'none',
                          '&:hover': { bgcolor: '#152433' },
                        }}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        disableRipple
                        onClick={() => setCurrentTextSuggestionIndex(idx + 1)}
                        sx={{
                          bgcolor: '#1976d2',
                          color: 'white',
                          textTransform: 'none',
                          '&:hover': { bgcolor: '#1565c0' },
                        }}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            )
          })()}

        {/* One "updating" banner for both an in-progress regen and the window
            while we auto-retry a transient failure (before it flips to running). */}
        {hasProfile && !generating && isUpdating && (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
            icon={<CircularProgress size={18} />}
          >
            Updating your profile with your latest information… this can take a
            moment.
          </Alert>
        )}

        {hasProfile && regenFailed && insufficientData && !generating && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            We tried to update your profile with the information you added, but
            we need to ask you a few questions to finish.{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {profileDataAssessment
                ? 'Answer the questions above to complete the update.'
                : 'Press "Answer questions" below to complete the update.'}
            </Box>
          </Alert>
        )}

        {/* Fallback only after an auto-retry has itself failed. */}
        {hasProfile &&
          regenFailed &&
          !insufficientData &&
          autoRetryFailed &&
          !generating && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Your last profile update didn't finish.{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                Press "Update profile" to try again.
              </Box>
            </Alert>
          )}

        {hasProfile &&
          lastChange &&
          !generating &&
          !isRegenerating &&
          !regenFailed && (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              onClose={() => setLastChange(null)}
            >
              {lastChange.changes.length > 0 ||
              lastChange.warnings.length > 0 ? (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Profile updated with your latest information
                  </Typography>
                  {lastChange.changes.length > 0 && (
                    <>
                      <Typography variant="body2">What changed:</Typography>
                      <Box component="ul" sx={{ m: 0, pl: 3 }}>
                        {lastChange.changes.map((c, i) => (
                          <li key={i}>
                            <Typography variant="body2">{c}</Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                  {lastChange.warnings.length > 0 && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mt: 1 }}
                      >
                        Check these details are still right:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 3 }}>
                        {lastChange.warnings.map((w, i) => (
                          <li key={i}>
                            <Typography variant="body2">{w}</Typography>
                          </li>
                        ))}
                      </Box>
                    </>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Profile up to date — no changes made.
                </Typography>
              )}
            </Alert>
          )}

        {/* Action Buttons — hidden while the questions gatekeeper wizard is open,
            so the only action there is the wizard's own Next/Skip. */}
      </Box>

      {/* Primary actions — hidden while the questions gatekeeper wizard is open
          (its own Next/Skip is the only action there) and while generating. */}
      {!profileDataAssessment && !generating && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            gap: isMobile ? 1 : 2,
            mt: 2,
            mb: 3,
            ml: isMobile ? 0 : 1,
          }}
        >
          {profileIsEmpty ? (
            <Button
              variant="contained"
              disableRipple
              sx={{
                color: 'white',
                bgcolor: '#1C3045',
                textTransform: 'none',
                borderRadius: 2,
                fontSize: '16px',
                py: 1,
                px: 4,
                height: '45px',
              }}
              onClick={handleGenerateProfile}
              // No description gate: generation also uses uploaded docs/website, and
              // the backend assessment returns the questions wizard if data is thin.
              disabled={saving || disabled || !canWrite || isRegenerating}
              startIcon={<AutoAwesome />}
            >
              Regenerate from scratch
            </Button>
          ) : (
            !profileTextAssessment && (
              <Button
                variant="contained"
                disableRipple
                sx={{
                  color: 'white',
                  bgcolor: '#1C3045',
                  textTransform: 'none',
                  borderRadius: 2,
                  fontSize: '16px',
                  py: 1,
                  px: 8,
                  height: '45px',
                }}
                onClick={() => handleSaveProfile()}
                disabled={saving || !canWrite || !hasUnsavedChanges}
                startIcon={
                  saving && <CircularProgress size={16} color="inherit" />
                }
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )
          )}

          {regenFailed && (insufficientData || autoRetryFailed) && (
            <Button
              variant="contained"
              disableRipple
              sx={{
                color: 'white',
                bgcolor: '#1C3045',
                textTransform: 'none',
                borderRadius: 2,
                fontSize: '16px',
                py: 1,
                px: 4,
                height: '45px',
              }}
              // Insufficient data can't be fixed by retrying the background regen —
              // open the data-suggestion wizard (prefilled with the questions the
              // failed job computed). Other failures get the plain retry.
              onClick={
                insufficientData
                  ? openSuggestionsWizard
                  : handleTriggerProfileUpdate
              }
              disabled={saving || regenerating || disabled || !canWrite}
              startIcon={<AutoAwesome />}
            >
              {insufficientData
                ? 'Answer questions'
                : regenerating
                  ? 'Starting…'
                  : 'Update profile'}
            </Button>
          )}

          {hasProfile && (
            <Button
              variant="text"
              disableRipple
              onClick={() => setShowHistory(true)}
              sx={{
                color: '#1C3045',
                textTransform: 'none',
                fontSize: '14px',
                height: '45px',
              }}
            >
              See previous versions
            </Button>
          )}

          {success ? (
            <Alert severity="success" sx={{ py: 0, alignItems: 'center' }}>
              Profile saved!
            </Alert>
          ) : error ? (
            <Alert severity="error" sx={{ py: 0, alignItems: 'center' }}>
              {error.message}
            </Alert>
          ) : null}
        </Box>
      )}

      {generating ? (
        <Box
          sx={{
            height: '260px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px dashed #ccc',
            borderRadius: 1,
            backgroundColor: '#f9f9f9',
          }}
        >
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
            Generating your profile...
          </Typography>
          <CircularProgress size={40} sx={{ color: '#1C3045' }} />
        </Box>
      ) : (
        // Lock + blur the box while the profile is updating so edits can't race the regen.
        <Box
          sx={{
            position: 'relative',
            opacity: isUpdating ? 0.5 : 1,
            pointerEvents: isUpdating ? 'none' : 'auto',
          }}
        >
          <SpeechToText
            token={token}
            disabled={generating || isUpdating}
            onTranscription={(text) =>
              setProfileText((prev) => (prev ? `${prev}\n\n${text}` : text))
            }
          />
          <TextField
            id="profile-edit"
            aria-label="Funding profile"
            multiline
            rows={20}
            fullWidth
            value={profileText}
            disabled={generating || isUpdating}
            onChange={(e) => setProfileText(e.target.value)}
            placeholder="Write your organisation's funding profile here — or use “Regenerate from scratch” to generate one."
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#757575',
              },
            }}
          />
        </Box>
      )}

      <ProfileRevisionHistoryDialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        token={token}
        orgUuid={organisation.uuid}
        onRestored={() =>
          queryClient.invalidateQueries({
            queryKey: ['currentOrgs', user?.uuid],
          })
        }
      />
    </>
  )
}
